import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Button, CircularProgress, Stack, TextField } from "@mui/material";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

import {
  Privilege,
  PrivilegeCreateRequest,
  PrivilegeUpdate,
} from "../../types/crm";

const privilegeSchema = z.object({
  name: z.string().min(1, "Name is required").max(200, "Name too long"),
  category: z.string().min(1, "Category is required"),
  details: z.string().min(1, "Details is required"),
  points_required: z.coerce.number().min(0, "Points must be 0 or more"),
  start_date: z.string().min(1, "Start date is required"),
  end_date: z.string().optional().or(z.literal("")),
});

type FormValues = z.infer<typeof privilegeSchema>;

type PropsType = {
  privilege?: Privilege | null;
  onSubmit: (data: PrivilegeCreateRequest | PrivilegeUpdate) => Promise<void>;
  onCancel: () => void;
};

const PrivilegeForm = ({ privilege, onSubmit, onCancel }: PropsType) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isEditing = !!privilege;

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(privilegeSchema),
    defaultValues: {
      name: privilege?.name || "",
      category: privilege?.category || "",
      details: privilege?.details || "",
      points_required: privilege?.points_required || 0,
      start_date: privilege?.start_date?.slice(0, 10) || "",
      end_date: privilege?.end_date?.slice(0, 10) || "",
    },
  });

  const handleFormSubmit = async (data: FormValues) => {
    setIsSubmitting(true);
    try {
      await onSubmit(data as PrivilegeCreateRequest);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit(handleFormSubmit)}>
      <Stack spacing={3}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Name"
                required
                fullWidth
                error={!!errors.name}
                helperText={errors.name?.message}
                disabled={isSubmitting}
              />
            )}
          />

          <Controller
            name="category"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Category"
                required
                fullWidth
                error={!!errors.category}
                helperText={errors.category?.message}
                disabled={isSubmitting}
              />
            )}
          />
        </Stack>

        <Controller
          name="details"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Details"
              required
              fullWidth
              multiline
              rows={3}
              error={!!errors.details}
              helperText={errors.details?.message}
              disabled={isSubmitting}
            />
          )}
        />

        <Controller
          name="points_required"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Points Required"
              type="number"
              fullWidth
              error={!!errors.points_required}
              helperText={errors.points_required?.message}
              disabled={isSubmitting}
            />
          )}
        />

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <Controller
            name="start_date"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Start Date"
                type="date"
                required
                fullWidth
                slotProps={{ inputLabel: { shrink: true } }}
                error={!!errors.start_date}
                helperText={errors.start_date?.message}
                disabled={isSubmitting}
              />
            )}
          />

          <Controller
            name="end_date"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="End Date"
                type="date"
                fullWidth
                slotProps={{ inputLabel: { shrink: true } }}
                error={!!errors.end_date}
                helperText={errors.end_date?.message}
                disabled={isSubmitting}
              />
            )}
          />
        </Stack>

        <Stack
          direction="row"
          spacing={2}
          justifyContent="flex-end"
          sx={{ pt: 2 }}
        >
          <Button variant="outlined" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
          >
            {isEditing ? "Update Privilege" : "Add Privilege"}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default PrivilegeForm;
